import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { RailItem } from "@/hooks/use-onboarding-step";
import type { OnboardingScreen, OnboardingStepKey } from "@/types/onboarding";
import { AnserTip } from "@/components/ui/anser-tip";
import { AutosaveIndicator, type AutosaveState } from "./AutosaveIndicator";
import { OnboardingDots, OnboardingRail } from "./OnboardingRail";
import {
  OnboardingUserMenu,
  type OnboardingUserMenuUser,
} from "./OnboardingUserMenu";

interface OnboardingShellProps {
  screen: OnboardingScreen;
  railItems: RailItem[];
  onRailClick: (key: OnboardingStepKey) => void;
  /** "Steg 2 av 6", "Ungefär 10 minuter" or "Klart". */
  stepLabel: string;
  autosave: AutosaveState;
  /** Anser's tip for the screen; empty hides the bubble. */
  tip: string;
  user?: OnboardingUserMenuUser;
  onSignOut?: () => void;
  children: ReactNode;
}

/**
 * Chrome of the six-step onboarding (GRA-171): a header with the kicker, the
 * step label and the account menu; the rail with Anser's tip down the left on
 * large screens (dots in the header below); the step in a card. Tokens only,
 * so it renders in Classic and Lilac, light and dark.
 */
export default function OnboardingShell({
  screen,
  railItems,
  onRailClick,
  stepLabel,
  autosave,
  tip,
  user,
  onSignOut,
  children,
}: OnboardingShellProps) {
  const { t } = useTranslation("onboarding");
  const showRail = screen !== "welcome";
  const showUserMenu = !!user && !!onSignOut;

  return (
    <div className="min-h-dvh bg-g-bg-app font-g text-g-ink-900 [font-variant-numeric:tabular-nums]">
      <header className="sticky top-0 z-20 border-b border-g-line bg-g-bg-app">
        <div className="mx-auto flex h-[68px] max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img
              src="/graftus-logo.svg"
              alt="Graftus"
              className="h-5 w-auto"
              style={{ filter: "var(--g-logo-filter)" }}
            />
            <span className="hidden text-[11px] font-bold uppercase tracking-wide text-g-ink-500 sm:inline">
              {t("meta.kicker")}
            </span>
          </div>
          <div className="flex items-center gap-4">
            {showRail ? (
              <div className="lg:hidden">
                <OnboardingDots items={railItems} />
              </div>
            ) : null}
            <span className="text-[13px] font-semibold text-g-ink-700">{stepLabel}</span>
            <span className="hidden sm:inline">
              <AutosaveIndicator state={autosave} />
            </span>
            {showUserMenu ? <OnboardingUserMenu user={user} onSignOut={onSignOut} /> : null}
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl gap-10 px-4 py-8 sm:px-6 lg:py-12">
        {showRail ? (
          <aside className="hidden w-64 shrink-0 flex-col gap-8 lg:flex">
            <OnboardingRail items={railItems} onClick={onRailClick} />
            <AnserTip text={tip} />
          </aside>
        ) : null}

        <main className="min-w-0 flex-1">
          <section
            key={screen}
            className="mx-auto w-full max-w-2xl rounded-g-xl border border-g-line bg-g-bg-card p-6 shadow-g-card animate-in fade-in duration-200 sm:p-8"
          >
            {children}
          </section>
          {showRail ? (
            <div className="mx-auto mt-6 max-w-2xl lg:hidden">
              <AnserTip text={tip} />
            </div>
          ) : null}
        </main>
      </div>
    </div>
  );
}

/** Title block at the top of a step: the "Steg n av 6" kicker is in the header, so only title and lead. */
export function StepHeading({ title, lead }: { title: string; lead?: string }) {
  return (
    <div className="mb-6 flex flex-col gap-2">
      <h1 className="text-[26px] font-extrabold leading-tight tracking-tight text-g-ink-900 sm:text-[30px]">
        {title}
      </h1>
      {lead ? <p className="text-[15px] leading-relaxed text-g-ink-500">{lead}</p> : null}
    </div>
  );
}
